import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { errors, paxRoute } from "../../../common";
import {
  AnalyzeAiCallsRequest,
  AnalyzeAiCallsResponse,
  SearchAiCallsRequest,
  SearchAiCallsResponse,
} from "./analytics";
import { AiCallDetail, AiCallParams, ListAiCallsQuery, ListAiCallsResponse } from "./schemas";

export const list = oc
  .meta(
    paxRoute({ auth: { kind: "session" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/ai/calls/v1alpha1",
      operationId: "ai.calls.list",
      summary: "List calls.",
      description:
        "Lists stored calls newest first, filtered by game, agent, agent version or purpose. A game's owner can list its calls; listing calls across games needs `agents.manage`.",
      inputStructure: "detailed",
      successDescription: "One page of calls.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...errors.unauthorized,
    ...errors.forbidden,
    ...errors.notFound,
  })
  .input(z.object({ query: ListAiCallsQuery }))
  .output(ListAiCallsResponse);

export const get = oc
  .meta(
    paxRoute({ auth: { kind: "session" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/ai/calls/v1alpha1/{callId}",
      operationId: "ai.calls.get",
      summary: "Get a call.",
      description:
        "Reads one call with the exact request and response bodies, when each streamed event arrived, and the earlier call that warmed its prompt cache.",
      inputStructure: "detailed",
      successDescription: "The call with both bodies.",
    }),
  )
  .errors({
    ...errors.unauthorized,
    ...errors.forbidden,
    NOT_FOUND: { message: "No stored call has that id." },
  })
  .input(z.object({ params: AiCallParams }))
  .output(AiCallDetail);

export const analyze = oc
  .meta(
    paxRoute({ auth: { kind: "session" }, minimumTier: null, cost: "search" }),
    openapi({
      method: "POST",
      path: "/api/ai/calls/v1alpha1/analyze",
      operationId: "ai.calls.analyze",
      summary: "Analyze calls.",
      description:
        "Adds up the calls a filter matches, in total, per value of one dimension, and per value over time. Needs `agents.manage`.",
      successDescription: "The totals.",
    }),
  )
  .errors({ ...errors.badRequest, ...errors.unauthorized, ...errors.forbidden })
  .input(AnalyzeAiCallsRequest)
  .output(AnalyzeAiCallsResponse);

export const search = oc
  .meta(
    paxRoute({ auth: { kind: "session" }, minimumTier: null, cost: "search" }),
    openapi({
      method: "POST",
      path: "/api/ai/calls/v1alpha1/search",
      operationId: "ai.calls.search",
      summary: "Search calls.",
      description:
        "Lists the calls a filter matches, latest, most expensive or slowest first, with how many match in all. Needs `agents.manage`.",
      successDescription: "One page of matching calls.",
    }),
  )
  .errors({ ...errors.badRequest, ...errors.unauthorized, ...errors.forbidden })
  .input(SearchAiCallsRequest)
  .output(SearchAiCallsResponse);
